import { filter, get, includes, map } from 'lodash';

import { COLLECTION_STORY_CARDS_TARGETING } from 'constants/collection';
import { SUPPORTED_LOCALES } from 'constants/locales';
import { MSRP_PRICE_TIER } from 'constants/pdp';
import Model from 'models';
import Settings from 'utils/Settings';

const ParentCollection = class extends Model {
  get handle() {
    return get(this._data, 'handle');
  }

  get id() {
    return get(this._data, 'id');
  }

  get cfId() {
    return get(this._data, 'cfId');
  }

  get name() {
    return get(this._data, ['localizedName', Settings.currentLocale]);
  }

  get masterProducts() {
    return get(this._data, 'masterProducts');
  }

  get parentCollectionItems() {
    return get(this._data, 'parentCollectionItems');
  }

  get priceTiers() {
    return get(this._data, 'priceTiers');
  }

  get isSaleCollection() {
    return !includes(this.priceTiers, MSRP_PRICE_TIER);
  }

  get topBanner() {
    // Defaulting to en-US since the content type isn't localized, only certain fields within are
    return get(this._data, ['topBanner', SUPPORTED_LOCALES.EN_US]);
  }

  get secondRowBanner() {
    return get(this._data, ['secondRowBanner', SUPPORTED_LOCALES.EN_US]);
  }

  get bottomBanner() {
    return get(this._data, ['bottomBanner', SUPPORTED_LOCALES.EN_US]);
  }

  get faqItems() {
    return get(this._data, ['faqItems', SUPPORTED_LOCALES.EN_US]);
  }

  get faqHeading() {
    return get(this._data, ['localizedFaqHeading', Settings.currentLocale]);
  }

  get storyCardPlacements() {
    const filteredCards = filter(get(this._data, 'storyCardPlacements', []), storyCard => {
      const localizedVisibility = get(storyCard, ['localizedVisibility', Settings.currentLocale]);
      return !localizedVisibility.includes('Hidden');
    });

    return map(filteredCards, storyCardPlacement => {
      const { localizedVisibility, row, storyCard, targeting } = storyCardPlacement;

      return {
        localizedVisibility: get(localizedVisibility, Settings.currentLocale),
        row: row - 1, // 1-indexed to 0-indexed
        storyCard,
        targeting,
      };
    });
  }

  get rightPillToggle() {
    return get(this._data, ['rightPillToggle', SUPPORTED_LOCALES.EN_US]);
  }

  get leftPillToggle() {
    return get(this._data, ['leftPillToggle', SUPPORTED_LOCALES.EN_US]);
  }

  get descriptionModule() {
    return get(this._data, ['localizedDescriptionModule', Settings.currentLocale]);
  }

  get experimentPageSections() {
    const sections = get(this._data, 'experimentPageSections', []);
    return map(sections, section => {
      const content = get(section, 'content', {});
      if (content._contentType === 'storyCardPlacement') {
        const { row, storyCard, localizedVisibility } = content;

        return {
          ...section,
          content: {
            localizedVisibility: get(localizedVisibility, Settings.currentLocale),
            row: row - 1, // 1-indexed to 0-indexed
            storyCard,
            targeting: COLLECTION_STORY_CARDS_TARGETING.DYNAMIC_YIELD,
            _contentType: content._contentType,
          },
        };
      }
      return section;
    });
  }

  /**
   * The fields below are getting deprecated soon
   */

  get description() {
    return get(this._data, ['localizedDescription', Settings.currentLocale]);
  }

  get bannerBgColor() {
    return get(this._data, 'bannerBgColor');
  }

  get childCollectionSortOrder() {
    return get(this._data, 'childCollectionSortOrder');
  }

  get image() {
    return get(this._data, ['localizedImage', Settings.currentLocale]);
  }
};

export default ParentCollection;
